import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Machine } from 'xstate';
import { useMachine } from '@xstate/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

import { useListener } from '~/emitter';

import { acceptTerms } from '~/api/routes/auth/termsConditions';
import { sendErrorService } from '~/services/error';

import Modal from '~/theseus/components/Modal';
import Checkbox from '~/theseus/components/Checkbox';
import Spinner from '~/theseus/components/Spinner';
import Button from '~/theseus/components/Button';
import { useAuthSelector, useAuthService } from '~/theseus/containers/Auth';

const termsMachine = Machine({
  id: 'rg-terms',
  initial: 'closed',
  states: {
    closed: {
      on: {
        OPEN: 'open',
      },
    },
    open: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            'TOS.accept': 'accepting',
            CLOSE: '#rg-terms.closed',
          },
        },
        accepting: {
          invoke: {
            id: 'set-user-terms',
            src: acceptTerms,
            onDone: 'accepted',
            onError: [
              {
                target: 'notAuthorized',
                // The rejection is the Response (status, plus `.data` holding
                // the parsed body — CherryPy's full error text, e.g.
                // "Error 403 Forbidden - neither owner nor admin")
                cond: (_, evt) => evt.data?.status === 403 && String(evt.data?.data || '').includes('neither owner nor admin'),
              },
              {
                target: 'failed',
                actions: (_, evt) => sendErrorService(new Error('Could not accept uzer terms'), 'terms-conditions', evt.data),
              }],
          },
        },
        accepted: {
          after: {
            2000: 'closing',
          },
        },
        // This state is used to allow for the modal to close smoothly
        closing: {
          after: {
            300: '#rg-terms.closed',
          },
          exit: 'continueAuthFlow',
        },
        notAuthorized: {
          type: 'final',
        },
        failed: {
          on: {
            'TOS.accept': 'accepting',
          },
        },
      },
    },
  },
});

const contentStyles: React.CSSProperties = {
  width: 540,
  height: 'unset',
  padding: 30,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const Content = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 10px;
`;

const Bold = styled(Content)`
  font-weight: 500;
`;

const successStyles = ({ show }) => {
  if (!show) {
    return css``;
  }

  return css`
    opacity: 1;
    scale: 1;
  `;
};

const Check = styled(FontAwesomeIcon, {
  // `show` only drives our styles; forwarding it to FontAwesomeIcon leaks it
  // onto the underlying <svg> and triggers a React non-boolean-attribute warning
  shouldForwardProp: (prop) => prop !== 'show',
})`
  transition: 200ms all ease-in;
  scale: 0;
  opacity: 0;
  position: absolute;
  right: -40px; 
  top: 10px;

  ${successStyles}
`;

const Form = styled.form`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;

  & > * {
    margin-bottom: 20px;
  }
`;

function TermsForm({
  onSubmit,
  isLoading,
  isError,
  isNotAuthorized,
  isSuccessful,
}) {
  const [termsChecked, setTermsChecked] = React.useState<boolean>(false);

  const toggleAcceptance = React.useCallback(() => {
    setTermsChecked(s => !s);
  }, []);

  return (
    <Form onSubmit={onSubmit}>
      <Container>
        <Checkbox htmlFor="agree-to-terms" checked={termsChecked}>
          <input
            id="agree-to-terms"
            type="checkbox"
            checked={termsChecked}
            onChange={toggleAcceptance}
            disabled={isNotAuthorized}
          />
          I Agree To These Terms
        </Checkbox>
        <Button type="submit" disabled={isLoading || !termsChecked || isNotAuthorized} size="lg" css={css`position: relative;`}>
          {
            isLoading
            && (
              <Spinner
                radius={10}
                weight={3}
                color="white"
                css={css`position: absolute; left: 10px; top: 10px;`}
              />
            )
          }
          Submit
          <Check
            icon={faCheck}
            color="green"
            size="lg"
            show={isSuccessful}
          />
        </Button>
      </Container>
      { isError && <p role="alert" css={css`color: red;`}>There was a problem accepting your terms and conditions </p> }
      { isNotAuthorized && <p role="alert" css={css`color: red;`}>You are not authorized to use the Facebook tool for this site. Please contact the site owner and ask to make you an admin</p> }
    </Form>
  );
}

const noop = () => {};

function TermsConditionsModal() {
  const needsToAcceptTerms = useAuthSelector(state => state.matches({ initializing: 'needsTos' }));
  const { send: sendAuthService } = useAuthService();

  const [state, send] = useMachine(termsMachine, {
    actions: {
      continueAuthFlow: () => sendAuthService('TOS.accepted'),
    },
  });

  React.useEffect(() => {
    if (needsToAcceptTerms) {
      send('OPEN');
    }
  }, [send, needsToAcceptTerms]);

  const acceptTermsConditions = React.useCallback((e) => {
    e.preventDefault();

    send('TOS.accept');
  }, [send]);

  useListener('open-tos', () => {
    send('OPEN');
  });

  // If user has not accepted the terms of service, we want to ensure that they are not able to
  // close it and use the tool without first explicitly accepting the terms
  const possiblyCloseModal = React.useCallback(() => {
    if (needsToAcceptTerms) {
      noop();
    } else {
      send('CLOSE');
    }
  }, [send, needsToAcceptTerms]);

  return (
    <Modal
      contentStyle={contentStyles}
      withClose={!needsToAcceptTerms}
      isOpen={state.matches('open')}
      onClose={possiblyCloseModal}
      onRequestClose={possiblyCloseModal}
      closeTimeoutMS={300}
    >
      <Bold>Do not post any listings from other offices unless you have permission or your MLS allows you to share other office&apos;s or broker&apos;s listings and/or property images on social media.</Bold>
      <Content>Sharing and posting property images that you do not have permission to may be a violation of your MLS Board terms and conditions. Real Geeks reserves the right to shut down your Ads Manager and your site if you violate any MLS rules. Real Geeks is not responsible for any fees, lawsuits, or MLS penalties incurred from using this tool. Post at your own risk.</Content>
      {
        needsToAcceptTerms
          && (
            <TermsForm
              onSubmit={acceptTermsConditions}
              isLoading={state.matches('open.accepting')}
              isError={state.matches('open.failed')}
              isNotAuthorized={state.matches('open.notAuthorized')}
              isSuccessful={state.matches('open.accepted')}
            />
          )
      }
    </Modal>
  );
}

export default TermsConditionsModal;
